/**
 * CuponsPage — CRUD de cupons do e-commerce (Configurações ERP).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  TicketPercent,
  Plus,
  Pencil,
  Ban,
  Loader2,
  RotateCcw,
  Search,
  Truck,
} from 'lucide-react'
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions'
import { useToast } from '../../../hooks/useToast'
import { Toast } from '../../../components'
import { couponsService } from '../../../services/couponsService'
import './ErpSettingsHubPage.css'
import './CockpitSettingsPage.css'
import './FretePorCepPage.css'
import './CuponsPage.css'

const EMPTY_FORM = {
  code: '',
  description: '',
  discountType: 'percent',
  discountValue: '',
  freeShipping: false,
  minOrderAmount: '0',
  validFrom: '',
  validUntil: '',
  active: true,
  maxUses: '',
}

function toInputDate(value) {
  if (!value) return ''
  return String(value).split('T')[0]
}

function formatMoney(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value) || 0)
}

function formatDiscount(coupon) {
  if (coupon.discountType === 'fixed') {
    return formatMoney(coupon.discountValue)
  }
  return `${Number(coupon.discountValue) || 0}%`
}

function formatValidity(coupon) {
  const from = toInputDate(coupon.validFrom)
  const until = toInputDate(coupon.validUntil)
  if (!from && !until) return 'Sem limite'
  if (from && until) return `${from} → ${until}`
  if (from) return `A partir de ${from}`
  return `Até ${until}`
}

function couponToForm(coupon) {
  return {
    code: coupon.code || '',
    description: coupon.description || '',
    discountType: coupon.discountType === 'fixed' ? 'fixed' : 'percent',
    discountValue: String(coupon.discountValue ?? ''),
    freeShipping: Boolean(coupon.freeShipping),
    minOrderAmount: String(coupon.minOrderAmount ?? '0'),
    validFrom: toInputDate(coupon.validFrom),
    validUntil: toInputDate(coupon.validUntil),
    active: coupon.active !== false,
    maxUses: coupon.maxUses == null ? '' : String(coupon.maxUses),
  }
}

function CuponsPage() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const { toast, showToast, hideToast } = useToast()

  const canEdit = hasPermission(MODULES.SETTINGS, ACTIONS.UPDATE)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [coupons, setCoupons] = useState([])
  const [search, setSearch] = useState('')
  const [activeOnly, setActiveOnly] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)

  const loadCoupons = useCallback(async () => {
    setLoading(true)
    try {
      const response = await couponsService.list({
        search: search.trim() || undefined,
        activeOnly: activeOnly ? 'true' : undefined,
      })
      if (response?.success) {
        setCoupons(response.data?.coupons || [])
      } else {
        showToast(response?.message || 'Erro ao carregar cupons', 'error')
      }
    } catch (error) {
      console.error(error)
      showToast('Erro ao carregar cupons', 'error')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeOnly])

  useEffect(() => {
    const t = setTimeout(() => {
      loadCoupons()
    }, 250)
    return () => clearTimeout(t)
  }, [loadCoupons])

  const openCreate = () => {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = (coupon) => {
    setEditingId(coupon.id)
    setForm(couponToForm(coupon))
    setModalOpen(true)
  }

  const closeModal = () => {
    if (saving) return
    setModalOpen(false)
    setEditingId(null)
    setForm(EMPTY_FORM)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!canEdit) return

    const payload = {
      code: form.code.trim().toUpperCase(),
      description: form.description.trim() || null,
      discountType: form.discountType,
      discountValue: Number(String(form.discountValue).replace(',', '.')),
      freeShipping: Boolean(form.freeShipping),
      minOrderAmount: Number(String(form.minOrderAmount).replace(',', '.')) || 0,
      validFrom: form.validFrom || null,
      validUntil: form.validUntil || null,
      active: form.active,
      maxUses: form.maxUses === '' ? null : Number(form.maxUses),
    }

    if (!payload.code) {
      showToast('Código é obrigatório', 'error')
      return
    }
    if (!Number.isFinite(payload.discountValue) || payload.discountValue < 0) {
      showToast('Valor do desconto inválido', 'error')
      return
    }
    if (payload.discountType === 'percent' && payload.discountValue > 100) {
      showToast('Percentual não pode ser maior que 100', 'error')
      return
    }
    if (payload.maxUses != null && (!Number.isFinite(payload.maxUses) || payload.maxUses < 1)) {
      showToast('Limite de usos inválido', 'error')
      return
    }
    if (payload.validFrom && payload.validUntil && payload.validFrom > payload.validUntil) {
      showToast('Data inicial deve ser anterior à final', 'error')
      return
    }

    setSaving(true)
    try {
      const response = editingId
        ? await couponsService.update(editingId, payload)
        : await couponsService.create(payload)

      if (!response?.success) {
        showToast(response?.message || 'Não foi possível salvar', 'error')
        return
      }

      showToast(editingId ? 'Cupom atualizado' : 'Cupom criado', 'success')
      closeModal()
      await loadCoupons()
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || 'Erro ao salvar'
      showToast(msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDeactivate = async (coupon) => {
    if (!canEdit || !coupon.active) return
    if (!window.confirm(`Desativar o cupom "${coupon.code}"?`)) return

    try {
      const response = await couponsService.deactivate(coupon.id)
      if (!response?.success) {
        showToast(response?.message || 'Erro ao desativar', 'error')
        return
      }
      showToast('Cupom desativado', 'success')
      await loadCoupons()
    } catch (error) {
      showToast(error?.response?.data?.message || 'Erro ao desativar', 'error')
    }
  }

  const activeCount = useMemo(
    () => coupons.filter((c) => c.active).length,
    [coupons]
  )

  return (
    <div className="erp-settings-dashboard frete-cep-page cupons-page">
      <button
        type="button"
        className="cockpit-settings-back-btn"
        onClick={() => navigate('/erp/configuracoes')}
      >
        <ArrowLeft size={14} /> Voltar às configurações
      </button>

      <div className="erp-settings-header">
        <div>
          <div className="frete-cep-title-row">
            <span className="cockpit-settings-icon-badge cupons-icon-badge">
              <TicketPercent size={20} />
            </span>
            <h1 className="erp-settings-title">
              Cupons <span className="erp-settings-title-highlight">E-commerce</span>
            </h1>
          </div>
          <p className="erp-settings-subtitle">
            Códigos promocionais · {activeCount} ativo(s) · {coupons.length} listado(s)
          </p>
        </div>
        <div className="erp-settings-header-actions">
          <button
            type="button"
            className="erp-settings-reload-btn"
            onClick={loadCoupons}
            disabled={loading}
          >
            <RotateCcw size={16} className={loading ? 'spinning' : ''} />
            Atualizar
          </button>
          {canEdit && (
            <button type="button" className="frete-cep-primary-btn" onClick={openCreate}>
              <Plus size={16} />
              Novo cupom
            </button>
          )}
        </div>
      </div>

      {!canEdit && (
        <div className="cockpit-settings-readonly-banner">
          Você tem permissão apenas de visualização. Peça UPDATE em Configurações para editar.
        </div>
      )}

      <div className="frete-cep-toolbar">
        <div className="frete-cep-search">
          <Search size={16} />
          <input
            type="search"
            placeholder="Buscar por código ou descrição..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="frete-cep-check">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          Somente ativos
        </label>
      </div>

      <div className="frete-cep-table-wrap">
        {loading ? (
          <div className="frete-cep-empty">
            <Loader2 size={28} className="spinning" />
            <p>Carregando cupons...</p>
          </div>
        ) : coupons.length === 0 ? (
          <div className="frete-cep-empty">
            <TicketPercent size={36} />
            <p>Nenhum cupom cadastrado</p>
          </div>
        ) : (
          <table className="frete-cep-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Desconto</th>
                <th>Frete</th>
                <th>Validade</th>
                <th>Usos</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {coupons.map((coupon) => (
                <tr key={coupon.id} className={!coupon.active ? 'is-inactive' : ''}>
                  <td>
                    <strong>{coupon.code}</strong>
                    {coupon.description && (
                      <div className="cupons-desc">{coupon.description}</div>
                    )}
                  </td>
                  <td>{formatDiscount(coupon)}</td>
                  <td>
                    {coupon.freeShipping ? (
                      <span className="cupons-badge cupons-badge--shipping">
                        <Truck size={12} /> Grátis
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{formatValidity(coupon)}</td>
                  <td>
                    {coupon.usesCount}
                    {coupon.maxUses != null ? ` / ${coupon.maxUses}` : ''}
                  </td>
                  <td>
                    <span className={`cupons-badge ${coupon.active ? 'is-active' : 'is-off'}`}>
                      {coupon.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="frete-cep-actions">
                    {canEdit && (
                      <>
                        <button
                          type="button"
                          className="frete-cep-icon-btn"
                          title="Editar"
                          onClick={() => openEdit(coupon)}
                        >
                          <Pencil size={16} />
                        </button>
                        {coupon.active && (
                          <button
                            type="button"
                            className="frete-cep-icon-btn danger"
                            title="Desativar"
                            onClick={() => handleDeactivate(coupon)}
                          >
                            <Ban size={16} />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <div className="frete-cep-modal-overlay" onClick={closeModal}>
          <div
            className="frete-cep-modal cupons-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="frete-cep-modal-header">
              <h2>{editingId ? 'Editar cupom' : 'Novo cupom'}</h2>
              <button type="button" onClick={closeModal} disabled={saving}>
                ×
              </button>
            </div>
            <form className="frete-cep-modal-body" onSubmit={handleSave}>
              <div className="frete-cep-form-grid">
                <label>
                  Código *
                  <input
                    value={form.code}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))
                    }
                    placeholder="MEDIK10"
                    required
                    disabled={saving}
                  />
                </label>
                <label>
                  Tipo de desconto *
                  <select
                    value={form.discountType}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, discountType: e.target.value }))
                    }
                    disabled={saving}
                  >
                    <option value="percent">Percentual (%)</option>
                    <option value="fixed">Valor fixo (R$)</option>
                  </select>
                </label>
                <label>
                  Valor *
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.discountValue}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, discountValue: e.target.value }))
                    }
                    required
                    disabled={saving}
                  />
                </label>
                <label>
                  Pedido mínimo (R$)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.minOrderAmount}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, minOrderAmount: e.target.value }))
                    }
                    disabled={saving}
                  />
                </label>
                <label>
                  Válido de
                  <input
                    type="date"
                    value={form.validFrom}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, validFrom: e.target.value }))
                    }
                    disabled={saving}
                  />
                </label>
                <label>
                  Válido até
                  <input
                    type="date"
                    value={form.validUntil}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, validUntil: e.target.value }))
                    }
                    disabled={saving}
                  />
                </label>
                <label>
                  Limite de usos
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={form.maxUses}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, maxUses: e.target.value }))
                    }
                    placeholder="Ilimitado"
                    disabled={saving}
                  />
                </label>
                <label className="frete-cep-span-2">
                  Descrição
                  <input
                    value={form.description}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, description: e.target.value }))
                    }
                    placeholder="Opcional"
                    disabled={saving}
                  />
                </label>
              </div>

              <div className="cupons-toggles">
                <label className={`cupons-toggle ${form.freeShipping ? 'is-on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={form.freeShipping}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, freeShipping: e.target.checked }))
                    }
                    disabled={saving}
                  />
                  <Truck size={16} />
                  Frete grátis
                </label>
                <label className={`cupons-toggle ${form.active ? 'is-on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, active: e.target.checked }))
                    }
                    disabled={saving}
                  />
                  Cupom ativo
                </label>
              </div>

              <div className="frete-cep-modal-footer">
                <button
                  type="button"
                  className="frete-cep-ghost-btn"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="frete-cep-primary-btn"
                  disabled={saving || !canEdit}
                >
                  {saving ? <Loader2 size={16} className="spinning" /> : null}
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Toast toast={toast} onClose={hideToast} />
    </div>
  )
}

export default CuponsPage
